import axios from "axios"
import type { AxiosInstance } from "axios"

/**
 * Creates an axios instance that preserves decimal precision beyond JavaScript's
 * ~16 significant digit limit in both directions:
 *
 * LOAD (response): Uses a custom transformResponse to wrap high-precision decimal
 * numbers in quotes before JSON.parse, so they survive as strings.
 *
 * SAVE (request): Replaces __RAWDEC:value__ placeholders with raw (unquoted) numeric
 * values in JSON bodies, sending full-precision numbers to the API.
 *
 * Usage: pass the returned instance to any NSwag client constructor as the second argument.
 */
export function createHighPrecisionAxios(): AxiosInstance {
	const instance = axios.create({
		transformResponse: [(data: string) => {
			if (typeof data === "string") {
				// Wrap decimal numbers with >15 digits after the decimal point in quotes
				// so they survive JSON.parse as strings instead of being truncated to float64
				data = data.replace(
					/([:,\[])\s*(-?\d+\.\d{14,})\s*([,}\]\r\n])/g,
					(_match, prefix, num, suffix) => {
						const clean = num.replace(/0+$/, "").replace(/\.$/, "")
						return `${prefix}"${clean}"${suffix}`
					}
				)
			}
			try {
				return JSON.parse(data)
			} catch {
				return data
			}
		}]
	})
	instance.interceptors.request.use((config) => {
		if (typeof config.data === "string" && config.data.includes("__RAWDEC:")) {
			config.data = config.data.replace(/"__RAWDEC:([^"]+)__"/g, "$1")
		}
		return config
	})
	return instance
}

/**
 * Patches an entity's toJSON method to inject __RAWDEC: placeholders for specified decimal fields.
 * The placeholders are later replaced by the high-precision axios interceptor to produce
 * raw JSON numbers with full precision.
 *
 * @param entity - The NSwag entity instance to patch
 * @param rawValues - Map of field names to their raw string values (from I2InputDecimal.rawValue)
 */
export function patchEntityForPrecision<T extends { toJSON: (data?: any) => any }>(
	entity: T,
	rawValues: Record<string, string | null>
): void {
	const originalToJSON = entity.toJSON.bind(entity)
	entity.toJSON = function (data?: any) {
		const result = originalToJSON(data)
		for (const [field, rawValue] of Object.entries(rawValues)) {
			if (rawValue !== null && rawValue !== "") {
				result[field] = `__RAWDEC:${rawValue}__`
			}
		}
		return result
	}
}
